/** The org's contact people — view, edit, add, remove.
 *
 * Backlog 2026-09-22 — reported gap: an org admin could not see or
 * update their own contact person. `OrganizationContact` has existed
 * since the partner-console org form, but the only way to manage it was
 * through the partner console, so a customer administering their own
 * org had no route to it at all.
 *
 * PUT replaces the whole set (the partner console's semantics), so this
 * card edits a local draft and saves the list in one shot rather than
 * diffing rows. Exactly one contact carries `is_default` — the backend
 * enforces that too, this is just the UI for choosing which.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PhoneInput from "@/components/PhoneInput";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError, isMfaRequired } from "@/lib/apiErrors";

export type OrgContact = {
  id?: number;
  name: string;
  email: string;
  phone: string;
  designation: string;
  is_default: boolean;
};

const EMPTY: OrgContact = {
  name: "",
  email: "",
  phone: "",
  designation: "",
  is_default: false,
};

export function OrgContactsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rows, setRows] = useState<OrgContact[]>([]);
  const [saved, setSaved] = useState(false);

  const contacts = useQuery({
    queryKey: ["org-contacts"],
    queryFn: async () =>
      (await api.get<{ contacts: OrgContact[] }>(ApiPaths.orgContacts)).data
        .contacts,
  });

  useEffect(() => {
    if (contacts.data) setRows(contacts.data.map((c) => ({ ...c })));
  }, [contacts.data]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = rows.filter((r) => r.name.trim());
      const res = await api.put<{ contacts: OrgContact[] }>(
        ApiPaths.orgContacts,
        { contacts: payload },
      );
      return res.data.contacts;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-contacts"], next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  const patch = (i: number, field: keyof OrgContact, value: string | boolean) =>
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)),
    );

  // Exactly one default — picking a new one clears the rest.
  const makeDefault = (i: number) =>
    setRows((prev) => prev.map((r, idx) => ({ ...r, is_default: idx === i })));

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.orgContacts.cardTitle", "Contact people")}
        </h2>
        <p className="text-xs text-nb-text-3">
          {t(
            "admin.orgContacts.cardHint",
            "Who to reach at your organization. The default contact is the primary point of contact.",
          )}
        </p>
      </header>

      {contacts.isLoading && (
        <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
      )}

      {rows.length === 0 && !contacts.isLoading && (
        <p className="text-xs text-nb-text-3">
          {t("admin.orgContacts.empty", "No contact people recorded yet.")}
        </p>
      )}

      <ul className="space-y-2">
        {rows.map((c, i) => (
          <li
            key={c.id ?? `new-${i}`}
            className="space-y-2 rounded-lg border border-nb-border bg-nb-bg/40 p-3"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="space-y-1 text-xs text-nb-text-2">
                <span>{t("admin.orgContacts.nameLabel", "Name")}</span>
                <input
                  className="input"
                  value={c.name}
                  onChange={(e) => patch(i, "name", e.target.value)}
                />
              </label>
              <label className="space-y-1 text-xs text-nb-text-2">
                <span>{t("admin.orgContacts.roleLabel", "Role")}</span>
                <input
                  className="input"
                  value={c.designation}
                  onChange={(e) => patch(i, "designation", e.target.value)}
                />
              </label>
              <label className="space-y-1 text-xs text-nb-text-2">
                <span>{t("admin.orgContacts.emailLabel", "Email")}</span>
                <input
                  className="input"
                  type="email"
                  value={c.email}
                  onChange={(e) => patch(i, "email", e.target.value)}
                />
              </label>
              {/* Uniformity 2026-09-23 — a bare text box here while every
                  other phone field in the app uses the shared E.164
                  input with a country/flag picker. The backend stores
                  E.164, so a number typed without a country code was
                  liable to be rejected or silently mis-stored. */}
              <label className="space-y-1 text-xs text-nb-text-2">
                <span>{t("admin.orgContacts.phoneLabel", "Phone")}</span>
                <PhoneInput
                  value={c.phone}
                  onChange={(v) => patch(i, "phone", v)}
                  aria-label={t("admin.orgContacts.phoneLabel", "Phone")}
                />
              </label>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-xs text-nb-text-2">
                <input
                  type="radio"
                  name="org-default-contact"
                  checked={c.is_default}
                  onChange={() => makeDefault(i)}
                />
                <span>
                  {t("admin.orgContacts.defaultLabel", "Primary contact")}
                </span>
              </label>
              <button
                type="button"
                className="btn-ghost text-xs text-cvp-danger hover:bg-cvp-danger/10"
                onClick={() =>
                  setRows((prev) => prev.filter((_, idx) => idx !== i))
                }
              >
                {t("admin.orgContacts.remove", "Remove")}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => setRows((prev) => [...prev, { ...EMPTY }])}
        >
          {t("admin.orgContacts.add", "Add a contact")}
        </button>
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending
            ? t("common.saving")
            : t("admin.orgContacts.saveButton", "Save contacts")}
        </button>
        {saved && (
          <span className="text-xs text-cvp-ok">
            {t("admin.orgContacts.savedAck", "Saved")}
          </span>
        )}
      </div>

      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {isMfaRequired(save.error)
            ? t("admin.mfaRequiredHint")
            : describeApiError(
                save.error,
                t("admin.orgContacts.saveErrorGeneric", "Could not save contacts."),
              )}
        </p>
      )}
    </section>
  );
}
